import type { SIUnit } from '@pn/core/domain/units';
import { isNil } from 'lodash-es';

const currencySymbols = ['$', 'CA$', 'US$'];

export type UnitOptions = {
  displaySymbol?: boolean;
  fractionDigits?: number;
  maxFractionDigits?: number;
  noThousandsSeparator?: boolean;
};

export const unitToString = (
  unit: SIUnit,
  options: UnitOptions = {}
): string => {
  const {
    displaySymbol = false,
    fractionDigits,
    maxFractionDigits,
    noThousandsSeparator = false,
  } = options;

  if (isNil(unit.value)) return '-';

  const isCurrency = currencySymbols.includes(unit.symbol);

  const formattedValue = unit.value.toLocaleString(undefined, {
    maximumFractionDigits:
      fractionDigits ?? maxFractionDigits ?? getMaxFractionDigits(unit.value),
    minimumFractionDigits: fractionDigits ?? 0,
    useGrouping: !noThousandsSeparator,
  });

  let symbolString = '';
  if (displaySymbol) {
    symbolString = formatUnitSymbol(unit.symbol);
    symbolString = isCurrency ? symbolString : ' ' + symbolString;
  }

  return isCurrency
    ? `${symbolString}${formattedValue}`
    : `${formattedValue}${symbolString}`;
};

function getMaxFractionDigits(value: number): number {
  if (value < 1) {
    return 3;
  } else if (value < 10) {
    return 2;
  } else {
    return 1;
  }
}

// prettier-ignore
const unitSymbolMap: Record<string, string> = {
  'm3': 'm³',
  'm3/d': 'm³/d',
  'e3m3': 'e³m³',
  'e3m3/d': 'e³m³/d',
  'm3/m3': 'm³/m³',
  'm3/e3m3': 'm³/e³m³',
  'C': '°C',
  'F': '°F',
  'kg/m3': 'kg/m³',
  'lb/ft3': 'lb/ft³',
};

export function formatUnitSymbol(symbol: string): string {
  return unitSymbolMap[symbol] ?? symbol;
}
